import {
  requiredCfm, toM3h, fmt, GENERAL_TARGET, WILDFIRE_TARGET, AHAM_ROOM_RATING_EACH, achievedEach, sqmToSqft, mToFt, requiredM3h,
} from '@/lib/calculator';
import { getModel, getTests, getFilterSet, getOffer } from '@/data/catalogue';
import { annualElectricity, computeCosts, DEFAULT_COSTS } from '@/lib/matching';

export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  publishedDate: string;
  /** Date the content was last reviewed against its sources */
  updatedDate: string;
  readTime: number;
  category: string;
  tags: string[];
  content: string;
}

const R = '2026-10-02';
const c = (a: number, h: number, each: number) => Math.ceil(requiredCfm({ areaSqFt: a, ceilingFt: h }, each) - 1e-9);
const G = GENERAL_TARGET.each;
const W = WILDFIRE_TARGET.each;

function sizeTable(sizes: number[], h = 8) {
  return `
| Room | General (${G} eACH) | Wildfire smoke (${W} eACH, smoke CADR) |
|---|---|---|
${sizes.map((a) => `| ${a} ft² | ${c(a, h, G)} CFM | ${c(a, h, W)} CFM |`).join('\n')}
`;
}

// Worked examples drawn from the shared catalogue so articles cannot drift from product pages.
const a150 = getModel('coway-airmega-150')!;
const a150Smoke = getTests(a150.id).find((t) => t.metric === 'smoke')!.cfm;
const a150Costs = computeCosts(a150, null, 1, DEFAULT_COSTS);
const a150Filter = getFilterSet(a150.id)!;
const a150Price = getOffer(a150.id)!.priceUsd;
const metricRoom = { areaSqFt: sqmToSqft(20), ceilingFt: mToFt(2.4) };

export const blogPosts: BlogPost[] = [
  {
    slug: 'choosing-an-air-purifier',
    title: 'Choosing an air purifier: the questions that actually matter',
    metaTitle: 'How to choose an air purifier: problem, room, CADR, noise, filters, cost',
    metaDescription: 'A practical order of questions for choosing an air purifier: what problem, how big the room, which CADR, usable speed, filters, support and cost.',
    excerpt: 'Start with the problem and the room, not the brand. Then check output at a speed you will tolerate and what filters will cost.',
    publishedDate: R, updatedDate: R, readTime: 6, category: 'Basics', tags: ['buying', 'CADR'],
    content: `
## 1. What problem are you solving?

Purifiers with HEPA-type filters remove **airborne particles**: smoke particles, pollen, dust, pet dander. They do not deal with gases, odours, CO₂, carbon monoxide or the moisture behind mould. If your main issue is a smell or a chemical, read [the gases guide](/blog/odours-gases-and-vocs) first. The [EPA guide to air cleaners](https://www.epa.gov/indoor-air-quality-iaq/guide-air-cleaners-home) puts source control and ventilation ahead of filtration.

## 2. How much air is in the room?

Multiply floor area by ceiling height. A ${fmt(300)} ft² room with an 8 ft ceiling holds 2,400 ft³.

## 3. How much clean air do you need?

This site uses **${G} equivalent air changes per hour** by default — a sizing choice just above AHAM's ${AHAM_ROOM_RATING_EACH} ACH room-rating basis. For wildfire smoke it uses AHAM's guidance (smoke CADR ≥ floor area at 8 ft), which works out to ${W} eACH. For 300 ft² × 8 ft that is **${c(300, 8, G)} CFM** generally, or **${c(300, 8, W)} CFM** of smoke CADR during smoke events. See [CADR and room size](/blog/cadr-and-room-size).

## 4. Which CADR?

CADR comes in three flavours: smoke, dust and pollen. Use the one that matches the problem. Smoke is the small-particle measure; it is not always the lowest of the three (Winix publishes 253 smoke vs 252 dust for the 5510).

## 5. What will it deliver at a speed you can live with?

Maximum-speed CADR is what is published. Most people run purifiers slower. A noise range like "22–52 dB" does not tell you the output at 22 dB. Where output at a quiet setting is not published, treat it as unknown.

## 6. Ionizers and other electronic stages

Some units add an ionizer (Winix PlasmaWave, Coway's bipolar stage). If you would rather avoid them, check whether it can be switched off — and whether the published CADR was measured with it off. Many manufacturers do not say. CARB certification limits ozone emissions; [CARB itself says](https://ww2.arb.ca.gov/list-carb-certified-air-cleaning-devices) it does not establish cleaning effectiveness.

## 7. Filters, availability and cost

Price the replacement filters per year, check they are actually in stock, and confirm the exact model they fit. See [filters and ownership costs](/blog/filters-and-ownership-costs).

## About viruses and health claims

A purifier can lower airborne particle concentrations in a room. That is not the same as a measured reduction in illness, and we do not make health claims. Public-health bodies treat filtration as one layer alongside ventilation.
`,
  },
  {
    slug: 'cadr-and-room-size',
    title: 'Understanding CADR and room size',
    metaTitle: 'CADR explained: smoke, dust, pollen, CFM vs m³/h, eACH and ceiling height',
    metaDescription: 'What CADR means, how to convert CFM and m³/h, what equivalent air changes per hour are, and why manufacturer room sizes disagree.',
    excerpt: 'CADR is the clean air a purifier delivers. Room sizes on the box depend on assumptions you should check.',
    publishedDate: R, updatedDate: R, readTime: 5, category: 'Basics', tags: ['CADR', 'ACH'],
    content: `
## What CADR is

Clean Air Delivery Rate is the volume of particle-free air a purifier effectively delivers, measured under AHAM AC-1 for smoke, dust and pollen. US figures are in CFM (cubic feet per minute). **1 CFM ≈ 1.699 m³/h.**

## The formula

- Required CADR (CFM) = room volume (ft³) × eACH ÷ 60
- Required CADR (m³/h) = room volume (m³) × eACH
- Achieved eACH = CADR × 60 ÷ room volume (ft³)

## Worked examples

- 300 ft² × 8 ft at ${G} eACH: 2,400 × ${G} ÷ 60 = **${c(300, 8, G)} CFM** (≈ ${fmt(toM3h(requiredCfm({ areaSqFt: 300, ceilingFt: 8 }, G)), 1)} m³/h).
- 300 ft² × 10 ft: **${c(300, 10, G)} CFM** — taller ceilings scale linearly.
- 20 m² × 2.4 m in metric: 48 m³ × ${G} = **${fmt(requiredM3h(metricRoom, G), 0)} m³/h** (${c(metricRoom.areaSqFt, metricRoom.ceilingFt, G)} CFM).
- A setup with 250 CFM in 300 ft² × 8 ft achieves ${fmt(achievedEach(250, { areaSqFt: 300, ceilingFt: 8 }), 2)} eACH.

${sizeTable([100, 150, 200, 300, 400, 500])}

## Why box room sizes disagree

AHAM's room rating assumes ${AHAM_ROOM_RATING_EACH} ACH and an 8 ft ceiling ([AHAM ACH FAQ](https://ahamverifide.org/ach-faq/)). Many manufacturers also print "cleans X ft² once per hour" figures based on 1 ACH, which are about five times larger. Compare CADR, not headline square footage.

## Equivalent, not literal

"Equivalent" air changes assume the room's air mixes well. Furniture, closed doors and placement change the real result.
`,
  },
  {
    slug: 'air-purifier-bedroom-guide',
    title: 'Sizing a purifier for a bedroom',
    metaTitle: 'Bedroom air purifier sizing: output at a speed you can sleep with',
    metaDescription: 'How to size a bedroom air purifier when quiet operation matters, and why maximum-speed CADR and minimum noise figures should not be combined.',
    excerpt: 'For bedrooms, the output you get at a tolerable noise level matters more than the number on the box.',
    publishedDate: R, updatedDate: R, readTime: 4, category: 'Rooms', tags: ['bedroom', 'noise'],
    content: `
## Target

A 12 × 12.5 ft bedroom (150 ft², 8 ft ceiling) needs **${c(150, 8, G)} CFM** at ${G} eACH.

## The noise problem

Manufacturers publish CADR at maximum speed and noise as a range. The "22 dB" figure is the lowest speed, which delivers far less air. You cannot pair the two.

Independent testers sometimes publish output at a fixed sound level. For example, HouseFresh reported an estimated 128 CFM PM1 CADR below 45 dBA for the Levoit Vital 200S — their own lab estimate, not an AHAM figure. Where that kind of data is missing, quiet-speed suitability is unknown, and our calculator says so.

## Practical tips

- A larger unit run at a lower speed can be quieter than a small unit flat out — if its output at that speed is known.
- Check whether lights can be turned off.
- Put the unit where air can circulate, not boxed into a corner.

## Nurseries

A HEPA purifier removes particles. It does **not** remove gases from new furniture or paint; ventilation and low-emission products matter more. Keep the unit and cord out of reach.
`,
  },
  {
    slug: 'air-purifier-for-wildfire-smoke',
    title: 'Wildfire smoke: sizing and setup',
    metaTitle: 'Air purifier for wildfire smoke: smoke CADR sizing (AHAM rule) and setup',
    metaDescription: 'How to size a purifier for wildfire smoke using smoke CADR and AHAM\'s floor-area guidance, plus EPA setup advice. No safety guarantees.',
    excerpt: 'Use smoke CADR, size at roughly smoke CADR = floor area (8 ft ceiling), and create one cleaner room.',
    publishedDate: R, updatedDate: R, readTime: 5, category: 'Problems', tags: ['wildfire', 'smoke'],
    content: `
## The sizing rule

AHAM recommends a **smoke CADR at least equal to the room's floor area** in ft² for an 8 ft ceiling ([AHAM](https://ahamverifide.org/using-an-air-cleaner-to-manage-wildfire-smoke/)). Scaled by volume for other ceilings, that is ${W} equivalent air changes per hour. Our calculator applies it automatically when you pick wildfire smoke, and checks **smoke CADR only**.

${sizeTable([100, 150, 200, 300, 400])}

These are filtration targets. They do not guarantee a safe exposure level, and other agencies use different rules of thumb — the [EPA's guidance](https://www.epa.gov/wildfires/indoor-air-filtration) is broadly similar in approach.

## Worked example

An Airmega 150 has a published smoke CADR of ${a150Smoke} CFM. That meets the target for rooms up to about ${Math.floor((a150Smoke * 60) / (W * 8))} ft² at 8 ft — at maximum speed.

## Setup

- Choose one room as the cleaner-air room and close it up.
- Keep windows closed when it is safe; recirculate your HVAC; consider a higher-MERV furnace filter if your system can take it.
- Run the purifier on high while smoke is present.
- Expect to replace filters sooner after a smoke event.
- Check local air-quality reports (AirNow in the US).

A DIY box-fan filter can be a stopgap — see [DIY air cleaners](/blog/diy-air-cleaners).
`,
  },
  {
    slug: 'dust-pets-and-cleaning',
    title: 'Dust, pets and cleaning: what a purifier can and cannot do',
    metaTitle: 'Air purifiers for dust and pets: airborne vs settled particles',
    metaDescription: 'Purifiers catch what is in the air. Settled dust, hair and dander still need cleaning. How to size for pets and dust.',
    excerpt: 'A purifier only catches particles while they are airborne. Floors, bedding and furniture are still your job.',
    publishedDate: R, updatedDate: R, readTime: 4, category: 'Problems', tags: ['dust', 'pets'],
    content: `
## Airborne vs settled

Visible dust on surfaces is mostly large particles that settle within minutes. A purifier helps with what is still in the air, and over time can mean less settling — but it will not replace cleaning. The [r/AirPurifiers FAQ](https://www.reddit.com/r/AirPurifiers/wiki/index/faqs/) has good, realistic expectations on this.

## Sizing

Pet dander and dust are checked against **dust CADR**. A 200 ft² × 8 ft room needs **${c(200, 8, G)} CFM** at ${G} eACH. If you also have pollen allergies, our calculator checks pollen CADR too, at the same speed.

## What helps alongside a purifier

- A vacuum with a sealed system and HEPA exhaust.
- Washing pet bedding and your own bedding weekly.
- A cleanable pre-filter, which catches hair and extends main-filter life.
- Keeping pets out of the bedroom if you react to them.
`,
  },
  {
    slug: 'odours-gases-and-vocs',
    title: 'Odours, gases and VOCs',
    metaTitle: 'Do air purifiers remove odours, gases and VOCs? Source, media and evidence',
    metaDescription: 'Particle CADR says nothing about gases. Source control and ventilation first; sorbent media depends on chemical, amount and testing.',
    excerpt: 'Gases need a different approach: find the source, ventilate, and treat carbon claims with care.',
    publishedDate: R, updatedDate: R, readTime: 5, category: 'Problems', tags: ['VOC', 'odour', 'carbon'],
    content: `
## Particle CADR is not gas removal

A HEPA filter captures particles. Gases pass through it. A thin carbon sheet in a typical purifier has limited capacity and can saturate quickly.

## What works first

- **Remove or seal the source.** New furniture or paint: ventilate heavily for the first weeks and choose low-emission products.
- **Ventilate.** Range hood vented outdoors for cooking; bathroom fans; open windows when outdoor air is clean.
- **Damp/musty smell:** that is moisture. Fix the damp; a purifier does not.
- **Unknown persistent smell, gas or burning smell:** call the utility or a professional. Fit a carbon monoxide alarm — purifiers do not remove CO.

## Sorbent media

Activated carbon and other sorbents can reduce some gases. Results depend on the specific chemical, media type and mass, humidity, airflow and replacement schedule. Heavy-carbon units (several kilograms) are a different class from typical purifiers.

## Testing exists — but is chemical-specific

[ANSI/AHAM AC-4-2022](https://www.aham.org/ItemDetail?Category=PADSTD&iProductCode=42022) is a method for measuring chemical-gas reduction. A result for one chemical does not prove performance for every odour or VOC. We do not yet hold chemical-specific test evidence for any model, so the calculator does not recommend anything for gases.

## CO₂

Purifiers do nothing for CO₂. Only ventilation does.
`,
  },
  {
    slug: 'air-purifier-running-cost',
    title: 'Filters and ownership costs',
    metaTitle: 'Air purifier running costs: filters per year, electricity and 3-year cost',
    metaDescription: 'How to estimate filter cost per year, electricity and 3-year ownership cost, including filters supplied with the purifier.',
    excerpt: 'Filter cost per year usually matters more than electricity. Count what comes in the box, and check filters are in stock.',
    publishedDate: R, updatedDate: R, readTime: 5, category: 'Costs', tags: ['filters', 'cost'],
    content: `
## The formulas

- **Electricity per year** = watts ÷ 1,000 × hours per day × 365 × price per kWh.
- **Filter cost per year (long run)** = price of the replacement set × 12 ÷ months it lasts.
- **Actual filter spend in 3 years** = number of sets you buy after the ones supplied with the purifier run out.

## Worked example (from our catalogue)

Coway Airmega 150, observed at $${a150Price} with a rated ${a150.ratedPowerW} W:

- Electricity at ${DEFAULT_COSTS.hoursPerDay} h/day and $${DEFAULT_COSTS.tariffUsdPerKwh}/kWh: about **$${fmt(annualElectricity(a150.ratedPowerW!, 1, DEFAULT_COSTS), 0)} per year** — using rated maximum power, so an upper estimate.
- ${a150Filter.retailName} (${a150Filter.contents}) at $${a150Filter.priceUsd} lasts ${a150Filter.cycleMonths} months: **$${fmt(a150Costs.annualFilterMaintenance!, 2)} per year** in the long run.
- The purifier ships with filters; the carbon filter is due at 6 months, so over 36 months you buy **${a150Costs.filterBoxes36} sets ($${fmt(a150Costs.filterSpend36!, 2)})**.
- Estimated 3-year total: **$${fmt(a150Costs.threeYear!, 0)}**.

Change the runtime, electricity price and filter life in the calculator — all costs update together.

## Things that change the answer

- Smoky or dusty homes use filters faster.
- Prices change; we record the date we saw each price and do not track them automatically.
- Check that genuine or compatible filters are actually in stock. A purifier with unavailable filters is a poor buy, and our calculator will not recommend one.
- Third-party filters can be cheaper but may not match the tested performance.

## When to replace

Follow the manufacturer's interval as a starting point, the unit's indicator if it has one, and shorten it after smoke events.
`,
  },
  {
    slug: 'diy-air-cleaners',
    title: 'DIY and fan-based air cleaners',
    metaTitle: 'DIY air cleaners (Corsi–Rosenthal box): what to know before building',
    metaDescription: 'Box-fan and furnace-filter air cleaners can deliver a lot of clean air cheaply, but output depends on the exact fan and filters.',
    excerpt: 'A box fan with MERV-13 filters can be very effective — but each build performs differently.',
    publishedDate: R, updatedDate: R, readTime: 3, category: 'DIY', tags: ['DIY', 'Corsi-Rosenthal'],
    content: `
## The idea

A box fan with one or more MERV-13 furnace filters taped to it. Versions with four or five filters in a cube are often called Corsi–Rosenthal boxes. The [EPA's DIY guidance](https://www.epa.gov/emergencies-iaq/diy-air-cleaners) covers safety and set-up.

## Why we do not list a CADR

Output depends on the specific fan, its speed, the filter brand and thickness, how many filters and how well it is sealed. One build's measured CADR does not apply to another. Until we record a specific, measured configuration, the calculator treats DIY output as unknown rather than guessing.

## Trade-offs

- Often a lot of clean air for the money.
- Bulky and can be loud at high speed.
- Use a fan with safety certification and do not leave it running unattended if the manufacturer advises against it.
- Kit versions (e.g. computer-fan designs) publish their own figures — check they match the exact configuration you buy.
`,
  },
];

/** Old article URLs → the rewritten article that replaces them. */
export const LEGACY_SLUGS: Record<string, string> = {
  'best-air-purifier-for-allergies': 'choosing-an-air-purifier',
  'hepa-vs-ionic-air-purifiers': 'choosing-an-air-purifier',
  'do-air-purifiers-help-with-covid': 'choosing-an-air-purifier',
  'best-air-purifier-under-100': 'air-purifier-running-cost',
  'air-purifier-for-baby-room': 'air-purifier-bedroom-guide',
  'best-air-purifier-for-pets': 'dust-pets-and-cleaning',
  'how-often-replace-air-purifier-filter': 'air-purifier-running-cost',
  'filters-and-ownership-costs': 'air-purifier-running-cost',
  'bedroom-sizing': 'air-purifier-bedroom-guide',
  'wildfire-particles': 'air-purifier-for-wildfire-smoke',
};

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}
export function getAllBlogPosts(): BlogPost[] {
  return blogPosts;
}
